import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import type { SelectChangeEvent } from '@mui/material';
import { useAuth } from '@core/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import type { AppLanguage } from '@app/i18n';
import { DEFAULT_LANGUAGE, normalizeAppLanguage, setAppLanguage } from '@app/i18n';

interface HeaderProps {
  onMenuClick: () => void;
}

/**
 * Application Header
 *
 * Responsive header with:
 * - Mobile: Hamburger menu button
 * - Desktop: Full navigation
 * - Language selector
 * - User info and logout
 */
export const Header = ({ onMenuClick }: HeaderProps) => {
  const { user, logout } = useAuth();
  const { t: tx, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const currentLanguage: AppLanguage = normalizeAppLanguage(i18n.language) ?? DEFAULT_LANGUAGE;

  const handleLanguageChange = (event: SelectChangeEvent<AppLanguage>) => {
    void setAppLanguage(event.target.value as AppLanguage);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = () => {
    handleMenuClose();
    navigate('/vozni-park/profil');
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    logout();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#ffffff',
        borderBottom: '3px solid',
        borderColor: '#340067', // Brand purple accent
        color: '#1f2937', // Dark text for white background
      }}
    >
      <Container maxWidth={false} disableGutters>
        <Toolbar
          sx={{
            px: { xs: 1, sm: 2 },
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={onMenuClick}
              sx={{
                mr: 1,
                color: '#6b7280',
                '&:hover': {
                  color: '#1e40af',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo & App Name */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 1, sm: 2 }}
            flexGrow={1}
            minWidth={0} // Allow flex item to shrink below content size
          >
            <Box
              component="img"
              src="/assets/logo.png"
              alt="Putevi Republike Srpske"
              sx={{
                height: { xs: 28, sm: 40 },
                width: 'auto',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          </Box>

          {/* Right side actions */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 0.5, sm: 2 }}
            flexShrink={0}
          >
            {/* Language selector - minimal style with only arrow */}
            <Select
              size="small"
              value={currentLanguage}
              onChange={handleLanguageChange}
              variant="standard"
              disableUnderline
              sx={{
                color: '#6b7280',
                minWidth: { xs: 60, sm: 90 },
                '& .MuiSelect-select': {
                  py: 0.5,
                  pr: '20px !important',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&:focus': {
                    bgcolor: 'transparent',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: '#6b7280',
                  right: 0,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                },
                '&:hover': {
                  color: '#1e40af',
                  '& .MuiSvgIcon-root': {
                    color: '#1e40af',
                  },
                },
              }}
            >
              <MenuItem value="sr-Latn">{tx('language.latin')}</MenuItem>
              <MenuItem value="sr-Cyrl">{tx('language.cyrillic')}</MenuItem>
            </Select>

            {/* Username (hide on mobile) */}
            {user?.username && !isMobile && (
              <Typography
                variant="body2"
                noWrap
                sx={{ color: '#6b7280' }}
              >
                {user.username}
              </Typography>
            )}

            {/* User menu button */}
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                color: '#6b7280',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                  color: '#1e40af',
                },
              }}
              aria-label="user menu"
              aria-controls={isMenuOpen ? 'user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? 'true' : undefined}
            >
              <AccountCircleIcon />
            </IconButton>

            {/* User dropdown menu */}
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'user-menu-button',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleAccountClick}>
                {tx('common.myAccount')}
              </MenuItem>
              <MenuItem onClick={handleLogoutClick}>
                {tx('common.logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

